import React, { useEffect, useRef, useState } from 'react';
import { Download, Mail, CreditCard, Cpu, AlertCircle, ExternalLink, Upload, Check } from 'react-feather';
import { apiClient } from '../shared/api/client';

// What the reconciliation view knows about a receipt. Both a matched expense
// and an orphan receipt can be shown here - they carry the same fields.
export interface ReceiptView {
  id: number;
  vendor: string;
  filename?: string | null;
  amount?: number | null;
  currency?: string | null;
  date?: string | null;
  sentAt?: string | null;
  subject?: string | null;
  note?: string | null;
  matchReason?: string | null;
  url?: string | null;
  hasFile: boolean;
  confidence?: string | null;
  transaction?: { date: string | null; amount: number; name: string } | null;
  transactionId?: number | null;
}

interface Props {
  receipt: ReceiptView;
  onClose: () => void;
  onChanged?: () => void;
}

const kr = (n: number) =>
  `${n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1">
    <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
    <span className="text-sm text-gray-900 text-right">{children}</span>
  </div>
);

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> =
  ({ icon, title, children }) => (
    <div className="mb-4">
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
        {icon}{title}
      </p>
      <div className="rounded-lg border border-gray-200 px-3 py-2">{children}</div>
    </div>
  );

const ReceiptModal: React.FC<Props> = ({ receipt, onClose, onChanged }) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(receipt.hasFile);
  const [error, setError] = useState<string | null>(null);
  const [showFile, setShowFile] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const backdropMouseDown = useRef(false);

  // The file sits behind the same auth as everything else, so it is fetched
  // with the token and turned into a local URL the viewer can render. A plain
  // link would only ever reach "Access token required".
  useEffect(() => {
    if (!receipt.hasFile) return;
    let url: string | null = null;
    let cancelled = false;
    apiClient.getBlob(`/mail/receipts/${receipt.id}/file`)
      .then(blob => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setFileUrl(url);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Kunde inte hämta filen.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [receipt.id, receipt.hasFile]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = () => {
    if (!fileUrl) return;
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = receipt.filename || `kvitto-${receipt.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const confirm = async () => {
    setError(null); setBusy(true);
    try {
      await apiClient.post(`/mail/receipts/${receipt.id}/confirm`, {});
      onChanged?.();
      onClose();
    } catch {
      setError('Kunde inte bekräfta kopplingen.');
    } finally { setBusy(false); }
  };

  const uploadFile = async (file: File) => {
    if (!receipt.transactionId) return;
    setError(null); setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('transactionId', String(receipt.transactionId));
      form.append('vendor', receipt.vendor);
      await apiClient.postForm('/mail/receipts/upload', form);
      onChanged?.();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Kunde inte ladda upp filen.');
    } finally { setBusy(false); }
  };

  const tx = receipt.transaction;
  const foreign = receipt.currency && receipt.currency !== 'SEK' ? receipt.currency : null;

  // Computing the rate here rather than trusting the sentence: if the figures
  // do not actually divide into a believable rate, that shows up immediately.
  const rate = tx && receipt.amount && foreign ? tx.amount / receipt.amount : null;
  const dayGap = tx?.date && receipt.date
    ? Math.round((new Date(tx.date).getTime() - new Date(receipt.date).getTime()) / 86400000)
    : null;

  const gapText = dayGap === null ? null
    : dayGap === 0 ? 'samma dag'
    : dayGap === 1 ? 'dagen efter kvittot'
    : dayGap > 0 ? `${dayGap} dagar efter kvittot`
    : `${Math.abs(dayGap)} dagar före kvittot`;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]"
      onMouseDown={e => { backdropMouseDown.current = e.target === e.currentTarget; }}
      onMouseUp={e => {
        if (backdropMouseDown.current && e.target === e.currentTarget) onClose();
        backdropMouseDown.current = false;
      }}
    >
      <div
        className="bg-white rounded-xl p-5 w-full max-w-lg max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between gap-3 mb-4">
          <h2 className="text-lg font-bold text-gray-900">{receipt.vendor}</h2>
          {receipt.confidence && (
            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
              receipt.confidence === 'säker' || receipt.confidence === 'bekräftad'
                ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
              {receipt.confidence}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <Section icon={<Mail size={12} />} title="Mailet">
            {receipt.sentAt && <Row label="Mottaget">{receipt.sentAt}</Row>}
            {receipt.subject && <Row label="Ämne">{receipt.subject}</Row>}
            <Row label="Kvittodatum">{receipt.date || 'saknas'}</Row>
            <Row label="Belopp">
              {receipt.amount != null
                ? `${receipt.amount.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${receipt.currency || ''}`
                : 'inget avläst'}
            </Row>
            {receipt.note && (
              <p className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-100">{receipt.note}</p>
            )}
            {receipt.url && (
              // The address comes from the mail, so it opens in its own tab
              // with no handle back to this one.
              <a
                href={receipt.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline mt-2 pt-2 border-t border-gray-100 block break-all"
              >
                <ExternalLink size={12} className="inline mr-1 -mt-0.5" />
                Öppna kvittot hos {receipt.vendor}
              </a>
            )}
          </Section>

          {tx && (
            <Section icon={<CreditCard size={12} />} title="Bankdragningen">
              <Row label="Datum">{tx.date || 'okänt'}{gapText && <span className="text-gray-500"> · {gapText}</span>}</Row>
              <Row label="Text">{tx.name}</Row>
              <Row label="Belopp">{kr(tx.amount)}</Row>
              {rate && (
                <Row label="Ger kursen">
                  {rate.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr/{foreign}
                </Row>
              )}
            </Section>
          )}

          {receipt.matchReason && (
            <Section icon={<Cpu size={12} />} title="Varför de hör ihop">
              <p className="text-sm text-gray-600">{receipt.matchReason}</p>
            </Section>
          )}

          {receipt.hasFile ? (
            <>
              {loading && <p className="text-sm text-gray-500">Hämtar kvittot…</p>}
              {error && (
                <p className="text-sm text-amber-700">
                  <AlertCircle size={13} className="inline mr-1 -mt-0.5" />{error}
                </p>
              )}
              {fileUrl && !showFile && (
                <button
                  onClick={() => setShowFile(true)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Visa den bifogade filen
                </button>
              )}
              {fileUrl && showFile && (
                <iframe
                  src={fileUrl}
                  title={receipt.filename || 'Kvitto'}
                  className="w-full h-[52vh] border border-gray-200 rounded-lg bg-gray-50"
                />
              )}
            </>
          ) : (
            <p className="text-xs text-gray-500">
              Ingen bifogad fil — leverantören skickade kvittot i mailtexten.
            </p>
          )}
        </div>

        {error && !receipt.hasFile && (
          <p className="text-sm text-amber-700 mt-3">
            <AlertCircle size={13} className="inline mr-1 -mt-0.5" />{error}
          </p>
        )}

        <div className="mt-4 space-y-2">
          {/* Uncertain until a person says otherwise. This is the saying. */}
          {receipt.confidence && receipt.confidence !== 'säker' && receipt.confidence !== 'bekräftad' && (
            <button
              onClick={confirm}
              disabled={busy}
              className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 disabled:opacity-40 transition-colors"
            >
              <Check size={15} className="inline mr-1.5 -mt-0.5" />Bekräfta att det stämmer
            </button>
          )}

          {receipt.transactionId && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) uploadFile(f);
                }}
              />
              <button
                onClick={() => fileInput.current?.click()}
                disabled={busy}
                className="w-full border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                <Upload size={15} className="inline mr-1.5 -mt-0.5" />
                {busy ? 'Laddar upp…' : receipt.hasFile ? 'Ersätt med egen fil' : 'Ladda upp kvitto själv'}
              </button>
            </>
          )}

          <div className="flex gap-2">
            {receipt.hasFile && (
              <button
                onClick={download}
                disabled={!fileUrl}
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
              >
                <Download size={15} className="inline mr-1.5 -mt-0.5" />Ladda ner
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Stäng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
